import { useState, useEffect } from 'react';
import { getUserById } from '../../../services/adminService';
import { getUserApplications } from '../../../services/userService';
import { updateAppStatus } from '../../../services/appStatusService';
import { AppStatus } from '../../../constants/appStatus';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../config/firebase';

export default function AdminUserDetailsLogic(userId, navigate) {
  const [user, setUser] = useState(null);
  const [applications, setApplications] = useState([]);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applicationsLoading, setApplicationsLoading] = useState(true);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  // Load user data
  useEffect(() => {
    const loadUserData = async () => {
      if (!userId) {
        console.log('No userId provided, redirecting to system settings');
        navigate('/system-settings');
        return;
      }

      console.log('Loading user data for userId:', userId);
      try {
        setLoading(true);
        const userData = await getUserById(userId);
        console.log('Loaded user data:', userData);
        
        if (!userData) {
          console.error('User not found for id:', userId);
          // Leave the user as null, the UI will handle the "not found" state
        }
        
        setUser(userData);
      } catch (error) {
        console.error('Error loading user:', error);
        alert('Error loading user data');
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, [userId, navigate]);

  // Load user applications
  useEffect(() => {
    const loadUserApplications = async () => {
      if (!userId) return;

      try {
        setApplicationsLoading(true);
        const apps = await getUserApplications(userId);
        console.log('Loaded applications:', apps);
        setApplications(apps);
      } catch (error) {
        console.error('Error loading applications:', error);
      } finally {
        setApplicationsLoading(false);
      }
    };

    loadUserApplications();
  }, [userId]);

  // Handle application selection
  const handleApplicationSelect = (app) => {
    console.log('Selected application:', app);
    setSelectedApplication(app);
  };

  // Handle back button
  const handleBackToSettings = () => {
    console.log('Navigating back to system settings');
    
    try {
      // Try to go back in history first if possible
      if (window.history.length > 1) {
        console.log('Going back in history');
        window.history.back();
      } else {
        // If no history, navigate directly
        console.log('No history, navigating directly to /admin/system-settings');
        navigate('/admin/system-settings');
      }
    } catch (error) {
      console.error('Error navigating back:', error);
      // Fallback to direct navigation
      navigate('/admin/system-settings');
    }
  };

  // Handle delete user
  const handleDeleteUser = async () => {
    if (!user) return;
    
    if (window.confirm(`Are you sure you want to delete user ${user.displayName || user.email}?`)) {
      try {
        // TODO: Implement user deletion in adminService
        // await deleteUser(userId);
        alert('User deletion feature coming soon');
        // navigate('/system-settings');
      } catch (error) {
        console.error('Error deleting user:', error);
        alert('Error deleting user');
      }
    }
  };

  // Handle reset password
  const handleResetPassword = async () => {
    if (!user) return;
    
    if (window.confirm(`Are you sure you want to send a password reset email to ${user.email}?`)) {
      try {
        // TODO: Implement password reset in adminService
        // await sendPasswordResetEmail(user.email);
        alert('Password reset feature coming soon');
      } catch (error) {
        console.error('Error sending password reset:', error);
        alert('Error sending password reset email');
      }
    }
  };

  // Handle update app to completed status
  const handleCompleteAppStatus = async (sendEmail = false) => {
    if (!selectedApplication) {
      alert('No application selected');
      return;
    }

    try {
      setIsUpdatingStatus(true);
      
      // First update the app status to APP_LIVE in the database
      await updateAppStatus(
        selectedApplication.id, 
        AppStatus.APP_LIVE, 
        "Status updated by admin"
      );
      
      // Also send email notification if requested
      if (sendEmail) {
        const sendAppStatusUpdateEmail = httpsCallable(functions, 'sendAppStatusUpdateEmail');
        await sendAppStatusUpdateEmail({
          userId: userId,
          appId: selectedApplication.id,
          sendEmail: true,
          userEmail: user.email,
          userName: user.displayName,
          appName: selectedApplication.name || selectedApplication.appName,
          currentStage: 'production'  // Set to production as this is for "Complete" status
        });
      }
      
      // Refresh application data
      const updatedApps = await getUserApplications(userId);
      setApplications(updatedApps);
      
      // Update selected application with the refreshed data
      const updatedApp = updatedApps.find(app => app.id === selectedApplication.id);
      if (updatedApp) {
        setSelectedApplication(updatedApp);
      }
      
      alert('Application status updated successfully' + (sendEmail ? ' and notification email sent' : ''));
    } catch (error) {
      console.error('Error updating application status:', error);
      alert('Error updating application status');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  return {
    user,
    applications,
    selectedApplication,
    loading,
    applicationsLoading,
    isUpdatingStatus,
    handleApplicationSelect,
    handleBackToSettings,
    handleDeleteUser,
    handleResetPassword,
    handleCompleteAppStatus
  };
} 